import { cacheKeys } from "@constants/cache-keys";
import { useAuthContext } from "@features/auth/contexts/AuthContext";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { invalidateUserDataQueries } from "@helpers/query-invalidation";
import { useActiveStudyPackCollectionId } from "@hooks/useActiveStudyPackCollectionId";
import { getMixedItemDistQueryKey } from "@hooks/useMixedItemDist";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { MixedItemDistAction } from "./types";

const DISTRIBUTION_TYPES = {
	COMMON: "COMMON_DISTRIBUTION",
	PRACTICE_TEST: "GET_PRATICETEST_DISTRIBUTION",
	STUDY_PACK: "GET_STUDYPACK_DISTRIBUTION",
} as const;

type Params = {
	productId?: string;
};
const useInvalidateQueries = (params?: Params) => {
	const queryClient = useQueryClient();
	const { userPublicMetadataId } = useAuthContext();

	const {
		currentExamContext: { examId },
	} = useExamContext();
	const { collectionId } = useActiveStudyPackCollectionId();

	const invalidateDistribution = useCallback(
		(distributionType: MixedItemDistAction) => {
			queryClient.invalidateQueries({
				queryKey: getMixedItemDistQueryKey({
					userPublicMetadataId,
					action: distributionType,
					examId,
					collectionId,
				}),
			});
		},
		[queryClient, userPublicMetadataId, collectionId, examId],
	);

	const invalidateProductMetadata = useCallback(() => {
		if (params?.productId) {
			queryClient.invalidateQueries({
				queryKey: [cacheKeys.productMetadata, params.productId],
			});
		}
	}, [queryClient, params?.productId]);

	const invalidateCommonDistribution = useCallback(
		() => invalidateDistribution(DISTRIBUTION_TYPES.COMMON),
		[invalidateDistribution],
	);

	const invalidatePracticeTestDistribution = useCallback(
		() => invalidateDistribution(DISTRIBUTION_TYPES.PRACTICE_TEST),
		[invalidateDistribution],
	);

	const invalidateStudyPackDistribution = useCallback(
		() => invalidateDistribution(DISTRIBUTION_TYPES.STUDY_PACK),
		[invalidateDistribution],
	);

	const invalidateAllDistributions = useCallback(() => {
		invalidateCommonDistribution();
		invalidatePracticeTestDistribution();
		invalidateStudyPackDistribution();
	}, [
		invalidateCommonDistribution,
		invalidatePracticeTestDistribution,
		invalidateStudyPackDistribution,
	]);

	const invalidateDistributionsAndMetadata = useCallback(() => {
		invalidateProductMetadata();
		invalidateAllDistributions();
	}, [invalidateProductMetadata, invalidateAllDistributions]);

	const invalidateUserData = useCallback(
		() => invalidateUserDataQueries(queryClient),
		[queryClient],
	);

	return {
		invalidateProductMetadata,
		invalidateAllDistributions,
		invalidateCommonDistribution,
		invalidatePracticeTestDistribution,
		invalidateStudyPackDistribution,
		invalidateDistributionsAndMetadata,
		invalidateUserData,
	};
};

export default useInvalidateQueries;
